import React, { useEffect } from "react";

import {
  Button,
  Flex,
  Stack,
  Text,
  Box,
  Skeleton,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalHeader,
  ModalCloseButton,
  useToast,
  Icon,
} from "@chakra-ui/core";
import {
  useHistory,
  useLocation,
  useParams,
  useRouteMatch,
} from "react-router";

import { applyJob, getJobById, getJobBySlug } from "../api/jobs.service";
import {
  formatCurrency,
  formatDate,
  openExternalLink,
} from "../lib/utils/utils";
import { Layout } from "../layouts/Layout";
import { Link } from "react-router-dom";
import useAuth from "../context/AuthContext";
import { FiExternalLink } from "react-icons/fi";
import styles from "./Homepage.module.css";
import CustomBreadcrumb from "../layouts/Breadcrumb";

const JobDetails = () => {
  const params = useParams();
  const history = useHistory();
  const { pathname } = useLocation();
  const {
    state: { user, isAuthenticated },
  } = useAuth();

  const initialJob = {
    id: "",
    title: "",
    about: "",
    mission: "",
    vision: "",
    business: {},
    registration_number: "",
    postal_address: "",
    office_address: "",
    phone_number: "",
    email: "",
    website: "",
    company_type: "",
    pitch_deck: "",
    job_model: "",
    created_at: "",
  };

  const [isFetchLoading, setIsFetchLoading] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  const [job, setJob] = React.useState(initialJob);

  const [isOpen, setIsOpen] = React.useState(false);
  const onClose = () => setIsOpen(false);
  const onOpen = () => setIsOpen(true);

  const toast = useToast();

  const formatJob = (job) => {
    if (job.includes("_")) {
      return job
        .split("_")
        .join(" ")
        .split(" ")
        .map((text) => {
          return text.charAt(0).toUpperCase() + text.slice(1);
        })
        .join(" ");
    }
  };

  const applyJobHandler = async () => {
    try {
      setIsLoading(true);
      if (
        user?.role === "user" &&
        isAuthenticated &&
        user?.email === job?.added_by?.email
      ) {
        toast({
          title: "Job Application",
          description: "You cannot apply to job you posted",
          status: "error",
          position: "top",
          isClosable: true,
        });
        setIsLoading(false);
        return;
      }
      if (isAuthenticated) {
        const { data } = await applyJob(params.id);
        setIsLoading(false);

        if (data.error) {
          toast({
            title: "Job Application",
            description: "You have already applied for this job",
            status: "error",
            position: "top",
            isClosable: true,
          });
          setIsLoading(false);
          return;
        }
        onOpen();
      } else {
        history.push(`/auth/sign-in?prevLocation=${pathname}`);
        toast({
          title: "Job Application",
          description: "You have to sign up or login to apply",
          status: "error",
          position: "top",
          isClosable: true,
        });
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchJob = async () => {
      try {
        setIsFetchLoading(true);
        const { data } = await getJobBySlug(params.slug);
        setJob(data);
        setIsFetchLoading(false);
      } catch (err) {
        setIsFetchLoading(false);
        console.log("Err", err);
      }
    };
    fetchJob();
  }, [params.id]);

  return (
    <Layout>
      <CustomBreadcrumb
        links={[
          { title: "Green Employment", link: "/jobs" },
          { title: "Jobs", link: "/jobs" },
          {
            title: job?.title,
            link: `/jobs/${job.id}`,
          },
        ]}
      />
      <Box px={["10", "20"]} py={["10", ""]} mt={["20", "140px"]}>
        <Text
          fontSize="2xl"
          fontWeight="800"
          textAlign="center"
          my="10"
          fontFamily="Familiar Pro"
        >
          Job Details
        </Text>
        <Modal isOpen={isOpen} onClose={onClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Job Application</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Box
                d="flex"
                justifyContent="center"
                alignItems="center"
                p="10"
                flexDirection="column"
              >
                <Text textAlign="center" my="10">
                  You have successfully applied for the job. You will be
                  notified soon.
                </Text>
                <Stack isInline>
                  <Button
                    onClick={() => {
                      history.push("/jobs");
                      onClose();
                    }}
                  >
                    View More
                  </Button>
                  <Link to="/dashboard">
                    <Button>View Applications</Button>
                  </Link>
                </Stack>
              </Box>
            </ModalBody>
          </ModalContent>
        </Modal>
        <Box bg="white" shadow="sm" className={styles.Container} p="10">
          <Flex align="center">
            <Skeleton isLoaded={!isFetchLoading}>
              <Box
                w="150px"
                h="150px"
                d="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Image
                  src={job.business.logo_url}
                  fallbackSrc="https://img.icons8.com/pastel-glyph/64/000000/suitcase--v4.png"
                />
              </Box>
            </Skeleton>

            <Stack ml="4">
              <Skeleton isLoaded={!isFetchLoading}>
                <Text fontSize="xl" fontWeight="800" fontFamily="Familiar Pro">
                  {job.title}
                </Text>
              </Skeleton>
              <Skeleton isLoaded={!isFetchLoading}>
                <Text fontSize="16px" fontWeight="500">
                  <strong>Sector:</strong> {job.job_sector?.name}
                </Text>
              </Skeleton>
              <Skeleton isLoaded={!isFetchLoading}>
                <Text fontSize="16px" fontWeight="500">
                  {/* job Type: {formatJob(job.business.name)} */}
                </Text>
              </Skeleton>
              <Skeleton isLoaded={!isFetchLoading}>
                <Text fontSize="16px" fontWeight="500">
                  <strong>Date Added:</strong> {formatDate(job.created_at)}
                </Text>
                <Text fontweight="800">
                  <em>Deadline: {formatDate(job.deadline)}</em>
                </Text>
              </Skeleton>
            </Stack>
          </Flex>
          <Stack my="8" spacing="8">
            <Box bg="white" py="4" px="6" shadow="sm">
              <Text fontSize="18px" fontWeight="800">
                Description
              </Text>
              <Skeleton isLoaded={!isFetchLoading}>
                <Text
                  fontSize="16px"
                  fontWeight="500"
                  w={["100%", "60%"]}
                  lineHeight="tall"
                >
                  {job.description}
                </Text>
              </Skeleton>
            </Box>

            <Flex justify="space-between">
              <Box bg="white" py="4" px="6" shadow="sm" flexBasis="50%">
                <Text fontSize="18px" fontWeight="800">
                  Certification Required
                </Text>
                <Skeleton isLoaded={!isFetchLoading}>
                  <Text fontSize="16px" fontWeight="500">
                    {job.job_certification?.name}
                  </Text>
                </Skeleton>
              </Box>
              <Box bg="white" py="4" px="6" shadow="sm" flexBasis="50%">
                <Text fontSize="18px" fontWeight="800">
                  Salary Range
                </Text>
                <Skeleton isLoaded={!isFetchLoading}>
                  <Text fontSize="16px" fontWeight="500">
                    {formatCurrency(job.salary_from)} -
                    {formatCurrency(job.salary_to)}
                  </Text>
                </Skeleton>
              </Box>
            </Flex>
            <Flex justify="space-between">
              <Box bg="white" py="4" px="6" shadow="sm" flex="1">
                <Text fontSize="18px" fontWeight="800">
                  Link
                </Text>
                <Skeleton isLoaded={!isFetchLoading}>
                  <Text fontSize="16px" fontWeight="500">
                    {job?.link ? job?.link : "N/A"}
                  </Text>
                  {job.link && (
                    <Icon
                      style={{ cursor: "pointer" }}
                      as={FiExternalLink}
                      onClick={() => openExternalLink(job?.link)}
                    />
                  )}
                </Skeleton>
              </Box>
              <Box bg="white" py="4" px="6" shadow="sm" flex="1">
                <Text fontSize="18px" fontWeight="800">
                  Level Required
                </Text>
                <Skeleton isLoaded={!isFetchLoading}>
                  <Text fontSize="16px" fontWeight="500">
                    {job.job_level ? job.job_level?.name : "N/A"}
                  </Text>
                </Skeleton>
              </Box>
              <Box bg="white" py="4" px="6" shadow="sm" flex="1">
                <Text fontSize="18px" fontWeight="800">
                  Country
                </Text>
                <Skeleton isLoaded={!isFetchLoading}>
                  <Text fontSize="16px" fontWeight="500">
                    {job.country ? job.country : "Ghana"}
                  </Text>
                </Skeleton>
              </Box>
            </Flex>
            <Flex justify="space-between">
              <Box bg="white" py="4" px="6" shadow="sm" flex="1">
                <Text fontSize="18px" fontWeight="800">
                  Available
                </Text>
                <Skeleton isLoaded={!isFetchLoading}>
                  <Text fontSize="16px" fontWeight="500">
                    {job.is_opened === 1 ? "No" : "Yes"}
                  </Text>
                </Skeleton>
              </Box>
              {/* <Box bg='white' py='4' px='6' shadow='sm' flex='1'>
                <Text fontSize='18px' fontWeight='800'>
                  Job Sponsored
                </Text>
                <Skeleton isLoaded={!isFetchLoading}>
                  <Text fontSize='16px' fontWeight='500'>
                    {job.is_sponsored === 1 ? "True" : "False"}
                  </Text>
                </Skeleton>
              </Box> */}
              {/* <Box bg='white' py='4' px='6' shadow='sm' flex='1'>
              <Text fontSize='18px' fontWeight='800'>
                job Model
              </Text>
              <Skeleton isLoaded={!isFetchLoading}>
                <Text fontSize='16px' fontWeight='500'>
                  {job.job_model ? job.job_model : "N/A"}
                </Text>
              </Skeleton>
            </Box> */}
            </Flex>
          </Stack>
          {/* {user?.role === "user" &&
            isAuthenticated &&
            user?.email !== job?.added_by?.email && (
              <Button
                alignSelf='end'
                w='20%'
                bg='green.400'
                color='white'
                _hover={{ bg: "green.400", color: "white" }}
                isLoading={isLoading}
                onClick={applyJobHandler}
              >
                Apply
              </Button>
            )} */}
          <Button
            alignSelf="end"
            w="20%"
            bg="green.400"
            color="white"
            _hover={{ bg: "green.400", color: "white" }}
            isLoading={isLoading}
            onClick={applyJobHandler}
          >
            Apply
          </Button>
          {/* {!isAuthenticated && (
            <Link to={`/auth/sign-in?prevLocation=${pathname}`}>
              <Button>Sign In to apply</Button>
            </Link>
          )} */}
        </Box>
      </Box>
    </Layout>
  );
};

export default JobDetails;
